import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getDatabase, ref, get, query, orderByChild, equalTo } from 'firebase/database';
import { getStorage, ref as storageRef, getDownloadURL } from 'firebase/storage';
import { Container, Row, Col, Button, Form, Alert, Modal, ListGroup, Badge, Table, Dropdown, Spinner } from 'react-bootstrap';
import { Plus, Calendar, Settings, Clock, AlertTriangle, CheckCircle, Check, BarChart3, MoreVertical, Trash2, FileText, CalendarClock, Camera, Pencil, Smartphone, ChevronDown, ChevronRight } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { BuildingCheckTemplate, InspectionCategory, TEMPLATE_CATEGORIES } from './types';
import { translateInspectionCategory, translateRegulatoryType } from '../../types/EnumTranslations';
import { useBuildComponentTypes } from '../../contexts/BuildComponentTypesContext';
import { ASSESSMENT_GROUPS } from '../../Helpers/assessmentGroups';
import { getAuth } from 'firebase/auth';
import { useBuilding } from '../../Helpers/BuildingContext';
import { useUserNames } from '../../Helpers/useUserNames';
import { useUsersData } from '../../Helpers/UsersContext';
import {
    BuildingCheckWithTemplate,
    CheckCompletion,
    formatDate,
    lastCompletedDate,
    formatInterval,
    formatDaysUntilDue,
    getDaysUntilDue,
    calculateNextDueDate,
    componentsMissingCheck,
    getEffectiveValues,
    loadBuildingChecks as loadChecksOfBuilding
} from './buildingChecksUtils';
import { buildingChecksApi } from '../../api/buildingChecksApi';
import { useCheckName } from '../../hooks/inspections/useCheckName';
import { groupBy } from '../../Helpers/groupBy';
import ConfirmationModal from '../../Components/ConfirmationModal';
import CheckLocationSection from './components/CheckLocationSection';
import './BuildingChecksPage.css';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { BreadcrumbComponent } from '../../Components/BreadCrumb';
import { formatDateOnlyForStorage } from '../../Helpers/DateUtils';
import { usePhotoUpload } from '../../hooks/usePhotoUpload';
import { useIsSmallScreen } from '../../hooks/useIsSmallScreen';


const BuildingChecksPage: React.FC = () => {
    const { id: buildingId } = useParams<{ id: string }>();
    // The statistics chart needs smaller axes on phones (Recharts props, not CSS)
    const isSmallScreen = useIsSmallScreen();
    const auth = getAuth();
    const user = auth.currentUser;
    const { currentBuilding } = useBuilding();
    const { userNames, loadUserNames } = useUserNames();
    const { users } = useUsersData();
    const { getTranslation } = useBuildComponentTypes();

    // Check if current user is admin
    const isAdmin = users?.find(u => u.key === user?.uid)?.admin || false;
    const [buildingChecks, setBuildingChecks] = useState<BuildingCheckWithTemplate[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [alert, setAlert] = useState<{ type: 'success' | 'danger'; message: string } | null>(null);
    
    // Completion modal state
    const [showCompletionModal, setShowCompletionModal] = useState(false);
    const [checkToComplete, setCheckToComplete] = useState<BuildingCheckWithTemplate | null>(null);
    const [modalAlert, setModalAlert] = useState<{ type: 'success' | 'danger'; message: string } | null>(null);
    const [completionForm, setCompletionForm] = useState({
        completedAt: '',
        measurementValue: '',
        notes: ''
    });

    // Photo upload hook for completion modal
    const {
        photos: completionPhotos,
        isDragOver,
        isUploading: isUploadingPhotos,
        uploadProgress,
        fileInputRef: completionFileInputRef,
        addPhotos: addCompletionPhotos,
        removePhoto: removeCompletionPhoto,
        clearPhotos: clearCompletionPhotos,
        uploadPhotos,
        handleFileSelect: handleCompletionFileSelect,
        dragHandlers: completionDragHandlers
    } = usePhotoUpload({
        onError: (message) => setModalAlert({ type: 'danger', message })
    });

    // Statistics modal state
    const [showStatsModal, setShowStatsModal] = useState(false);
    const [checkForStats, setCheckForStats] = useState<BuildingCheckWithTemplate | null>(null);
    const [checkResults, setCheckResults] = useState<CheckCompletion[]>([]);
    
    
    // Delete confirmation modal state
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [checkToDelete, setCheckToDelete] = useState<BuildingCheckWithTemplate | null>(null);

    // Category filter state
    const [categoryFilter, setCategoryFilter] = useState<string>('');
    // Templates whose group of checks is expanded
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

    // Snooze modal state
    const [showSnoozeModal, setShowSnoozeModal] = useState(false);
    const [checkToSnooze, setCheckToSnooze] = useState<BuildingCheckWithTemplate | null>(null);
    const [snoozeDate, setSnoozeDate] = useState('');

    const db = getDatabase();
    const checkName = useCheckName(currentBuilding);

    // Set document title
    useDocumentTitle(
        currentBuilding?.summary?.name
            ? `Kontroller - ${currentBuilding.summary.name}`
            : 'Kontroller'
    );

    // Load building checks and available templates
    useEffect(() => {
        const actualBuildingId = buildingId || currentBuilding?.key;
        if (actualBuildingId) {
            loadBuildingChecks(actualBuildingId);
        }
    }, [buildingId, currentBuilding]);

    const loadBuildingChecks = async (actualBuildingId: string) => {
        try {
            setLoading(true);

            setBuildingChecks(await loadChecksOfBuilding(actualBuildingId, currentBuilding?.summary?.name ?? ''));
        } catch (error) {
            console.error('Error loading building checks:', error);
            setAlert({ type: 'danger', message: 'Fel vid laddning av aktiviteter' });
        } finally {
            setLoading(false);
        }
    };

    const loadCheckResults = async (checkKey: string, actualBuildingId: string) => {
        try {
            const resultsRef = ref(db, `inspections/completions/${actualBuildingId}/${checkKey}`);
            const resultsSnapshot = await get(resultsRef);
            
            if (resultsSnapshot.exists()) {
                const resultsData = resultsSnapshot.val();
                const results: CheckCompletion[] = [];
                const uniqueUserIds = new Set<string>();
                
                // Process each completion (date as key)
                for (const [date, completionData] of Object.entries(resultsData)) {
                    const completion = completionData as CheckCompletion;
                    results.push({
                        ...completion,
                        completedAt: date // Use the date key as completedAt
                    });
                    
                    // Collect unique user IDs
                    if (completion.completedBy) {
                        uniqueUserIds.add(completion.completedBy);
                    }
                }
                
                // Sort by date (most recent first)
                results.sort((a, b) => new Date(b.completedAt).getTime() - new Date(a.completedAt).getTime());
                
                setCheckResults(results);
                
                // Load user names for all unique users
                await loadUserNames(Array.from(uniqueUserIds));
            } else {
                setCheckResults([]);
            }
        } catch (error) {
            console.error('Error loading check results:', error);
            setCheckResults([]);
        }
    };


    // Drives the row's status colour; same day count as the "om N dagar" text.
    const getDueDateStatus = (nextDueDate: string, advanceNotice: number): 'overdue' | 'due-soon' | 'upcoming' => {
        const daysUntilDue = getDaysUntilDue(nextDueDate);
        if (daysUntilDue < 0) return 'overdue';
        if (daysUntilDue <= advanceNotice) return 'due-soon';
        return 'upcoming';
    };

    const openCompletionModal = (check: BuildingCheckWithTemplate) => {
        setCheckToComplete(check);
        setCompletionForm({
            completedAt: formatDateOnlyForStorage(new Date()), // Default to today in Stockholm timezone
            measurementValue: '',
            notes: ''
        });
        setModalAlert(null); // Clear any previous modal alerts
        // Reset photo state
        clearCompletionPhotos();
        setShowCompletionModal(true);
    };

    const openStatsModal = async (check: BuildingCheckWithTemplate) => {
        const actualBuildingId = buildingId || currentBuilding?.key;
        if (!actualBuildingId) return;
        
        setCheckForStats(check);
        setShowStatsModal(true);
        await loadCheckResults(check.checkKey, actualBuildingId);
    };

    const handleCompleteCheck = async () => {
        if (!checkToComplete || !user || !actualBuildingId) {
            setAlert({ type: 'danger', message: 'Fel: Saknar nödvändig information' });
            return;
        }

        // Validate completion date is not in the future
        const today = formatDateOnlyForStorage(new Date());
        if (completionForm.completedAt > today) {
            setModalAlert({ type: 'danger', message: 'Slutförandedatum kan inte vara i framtiden.' });
            return;
        }

        // Validate measurement if required
        if (checkToComplete.template.takesMeasurement && (!completionForm.measurementValue || completionForm.measurementValue.trim() === '')) {
            setModalAlert({ type: 'danger', message: 'Mätvärde krävs för denna aktivitet. Vänligen ange ett värde.' });
            return;
        }

        try {
            setSaving(true);
            
            // One completion per check and day
            if (await buildingChecksApi.hasCompletionOn(actualBuildingId, checkToComplete.checkKey, completionForm.completedAt)) {
                setModalAlert({
                    type: 'danger',
                    message: `En aktivitet har redan registrerats för ${formatDate(completionForm.completedAt)}. Det går inte att registrera flera aktiviteter samma dag.`
                });
                setSaving(false);
                return;
            }

            // Photos first, under the completion (the only part of it clients write);
            // completeInspection then writes the record and moves the due date.
            if (completionPhotos.length > 0) {
                const basePath = `inspections/completions/${actualBuildingId}/${checkToComplete.checkKey}/${completionForm.completedAt}/photos`;
                await uploadPhotos({
                    storagePath: (photoKey) => `${basePath}/${photoKey}`,
                    dbPath: (photoKey) => `${basePath}/${photoKey}`
                });
            }

            await buildingChecksApi.completeBuildingCheck({
                buildingKey: actualBuildingId,
                checkKey: checkToComplete.checkKey,
                completedDate: completionForm.completedAt,
                summaryComment: completionForm.notes.trim(),
                checkLabel: checkName(checkToComplete, checkToComplete.template.name),
                ...(checkToComplete.template.takesMeasurement && completionForm.measurementValue && {
                    measurementValue: parseFloat(completionForm.measurementValue)
                })
            });

            // Clean up and reset
            clearCompletionPhotos();
            setAlert({ type: 'success', message: 'Kontroll markerad som klar!' });
            setShowCompletionModal(false);
            setCheckToComplete(null);
            setCompletionForm({ completedAt: '', measurementValue: '', notes: '' });
            loadBuildingChecks(actualBuildingId);
        } catch (error) {
            console.error('Error completing check:', error);
            setAlert({ type: 'danger', message: 'Fel vid markering av aktivitet som klar' });
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteCheck = async () => {
        if (!checkToDelete || !actualBuildingId) {
            setAlert({ type: 'danger', message: 'Fel: Saknar nödvändig information' });
            return;
        }

        try {
            setSaving(true);
            
            await buildingChecksApi.removeBuildingCheck(actualBuildingId, checkToDelete.checkKey);

            setAlert({ type: 'success', message: 'Kontroll borttagen framgångsrikt!' });
            setShowDeleteModal(false);
            setCheckToDelete(null);
            loadBuildingChecks(actualBuildingId);
        } catch (error) {
            console.error('Error deleting check:', error);
            setAlert({ type: 'danger', message: 'Fel vid borttagning av aktivitet' });
        } finally {
            setSaving(false);
        }
    };

    const handleSnoozeCheck = async () => {
        if (!checkToSnooze || !actualBuildingId || !snoozeDate) {
            setAlert({ type: 'danger', message: 'Fel: Saknar nödvändig information' });
            return;
        }

        // Validate snooze date is in the future
        const today = formatDateOnlyForStorage(new Date());
        if (snoozeDate <= today) {
            setAlert({ type: 'danger', message: 'Nytt förfallodatum måste vara i framtiden.' });
            return;
        }

        // Validate snooze date is after current due date
        if (snoozeDate <= checkToSnooze.nextDueDate) {
            setAlert({ type: 'danger', message: 'Nytt datum måste vara efter nuvarande förfallodatum.' });
            return;
        }

        try {
            setSaving(true);

            await buildingChecksApi.updateBuildingCheck(actualBuildingId, checkToSnooze.checkKey, { nextDueDate: snoozeDate });

            setAlert({ type: 'success', message: 'Förfallodatum har flyttats fram!' });
            setShowSnoozeModal(false);
            setCheckToSnooze(null);
            setSnoozeDate('');
            loadBuildingChecks(actualBuildingId);
        } catch (error) {
            console.error('Error snoozing check:', error);
            setAlert({ type: 'danger', message: 'Fel vid ändring av förfallodatum' });
        } finally {
            setSaving(false);
        }
    };

    const actualBuildingId = buildingId || currentBuilding?.key;

    // One check, in its template's group when the group is expanded
    const renderCheckRow = (check: BuildingCheckWithTemplate, inGroup: boolean) => {
        const dueStatus = getDueDateStatus(check.nextDueDate, check.template.advanceNotice);
        const daysText = formatDaysUntilDue(check.nextDueDate);
        return (
                <tr key={check.checkKey} className={`check-row--${dueStatus}${inGroup ? ' check-row--in-group' : ''}`}>
                    <td className="check-cell-activity">
                        <strong>{checkName(check, check.template.name)}</strong>
                        <div className="check-tags d-flex flex-wrap gap-1 mt-1 mb-2">
                        {check.template.category && (
                            <span className="check-tag">
                                {translateInspectionCategory(check.template.category)}
                            </span>
                        )}
                        {check.template.regulatoryType && (
                            <span className="check-tag check-tag--regulatory">
                                {translateRegulatoryType(check.template.regulatoryType)}
                            </span>
                        )}
                        {check.template.componentBaseTypeGroup && (
                            <span
                                className="check-tag check-tag--auto"
                                title="Auto-länkad till alla aktiva komponenter i denna grupp"
                            >
                                Auto: {ASSESSMENT_GROUPS[check.template.componentBaseTypeGroup].label}
                            </span>
                        )}
                        </div>
                        {check.template.estimatedTime > 0 && (
                            <div className="small text-muted">
                                <strong>Uppskattad tid:</strong> {check.template.estimatedTime} min
                            </div>
                        )}
                        {check.template.takesMeasurement && (
                            <div className="small text-muted mt-1">
                                <strong>Mätvärde i:</strong> {check.template.takesMeasurement.metric}
                                {check.template.takesMeasurement.acceptableRange && (
                                    <span>
                                        {check.template.takesMeasurement.acceptableRange.min !== undefined && check.template.takesMeasurement.acceptableRange.max !== undefined && (
                                            <span> ({check.template.takesMeasurement.acceptableRange.min} - {check.template.takesMeasurement.acceptableRange.max})</span>
                                        )}
                                        {check.template.takesMeasurement.acceptableRange.min !== undefined && check.template.takesMeasurement.acceptableRange.max === undefined && (
                                            <span> (min: {check.template.takesMeasurement.acceptableRange.min})</span>
                                        )}
                                        {check.template.takesMeasurement.acceptableRange.min === undefined && check.template.takesMeasurement.acceptableRange.max !== undefined && (
                                            <span> (max: {check.template.takesMeasurement.acceptableRange.max})</span>
                                        )}
                                    </span>
                                )}
                            </div>
                        )}
                    </td>
                    <td className="check-cell-due">
                        {/* Phones show the rows as cards without the table header */}
                        <div className="check-cell-label">Förfaller</div>
                        <div>{formatDate(check.nextDueDate)}</div>
                        <div className={`small check-due-text check-due-text--${dueStatus}`}>
                            {daysText}
                        </div>
                    </td>
                    <td className="check-cell-last">
                        <div className="check-cell-label">Senast genomförd</div>
                        {lastCompletedDate(check) ? (
                            <div>
                                <div className="small d-flex align-items-center">
                                    {formatDate(lastCompletedDate(check)!)}
                                    {lastCompletedDate(check) === formatDateOnlyForStorage(new Date()) && (
                                        <Check size={16} className="text-success ms-1" />
                                    )}
                                </div>
                                {check.template.takesMeasurement && check.lastMeasurement && (
                                    <div className="small text-muted">
                                        {check.lastMeasurement.value} {check.lastMeasurement.unit}
                                        {!check.lastMeasurement.withinRange && (
                                            <span className="text-danger ms-1">⚠</span>
                                        )}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <span className="text-muted small">Aldrig genomförd</span>
                        )}
                    </td>
                    <td className="check-cell-interval">
                        <div className="small">
                            <span className="check-cell-label">Intervall</span>
                            {formatInterval(getEffectiveValues(check, check.template).interval)}
                        </div>
                    </td>
                    <td className="check-cell-actions">
                        {/* Edit and delete are always visible; the rest sits in the menu (#1156) */}
                        <Dropdown align="end">
                            <Dropdown.Toggle variant="link" className="check-action-btn" id={`dropdown-${check.checkKey}`} aria-label="Fler åtgärder" title="Fler åtgärder">
                                <MoreVertical size={16} />
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                {check.template.category === InspectionCategory.ConditionAssessment ? (
                                    // Skickbedömning is completed in the iOS app only (D15)
                                    <Dropdown.Item disabled title="Skickbedömningen markeras som utförd i appen">
                                        <Smartphone size={16} className="me-2" />
                                        Utförs i appen
                                    </Dropdown.Item>
                                ) : (
                                    <Dropdown.Item onClick={() => openCompletionModal(check)}>
                                        <CheckCircle size={16} className="me-2" />
                                        Markera som utförd
                                    </Dropdown.Item>
                                )}
                                <Dropdown.Item onClick={() => openStatsModal(check)}>
                                    <BarChart3 size={16} className="me-2" />
                                    Visa statistik
                                </Dropdown.Item>
                                {isAdmin && (
                                    <Dropdown.Item onClick={() => {
                                        setCheckToSnooze(check);
                                        setSnoozeDate('');
                                        setShowSnoozeModal(true);
                                    }}>
                                        <CalendarClock size={16} className="me-2" />
                                        Flytta förfallodatum
                                    </Dropdown.Item>
                                )}
                            </Dropdown.Menu>
                        </Dropdown>
                        <Link
                            to={`/building/${buildingId}/checks/${check.checkKey}/edit`}
                            className="btn btn-link check-action-btn"
                            aria-label="Redigera"
                            title="Redigera"
                        >
                            <Pencil size={16} />
                        </Link>
                        {isAdmin && (
                            <Button
                                variant="link"
                                className="check-action-btn check-action-btn--danger"
                                onClick={() => {
                                    setCheckToDelete(check);
                                    setShowDeleteModal(true);
                                }}
                                aria-label="Radera"
                                title="Radera"
                            >
                                <Trash2 size={16} />
                            </Button>
                        )}
                    </td>
                </tr>
        );
    };

    // Checks grouped by template, soonest due first; each group sorted by due date
    const checkGroups = (() => {
        const visible = buildingChecks
            .filter(check => !categoryFilter || check.template?.category === categoryFilter)
            .sort((x, y) => x.nextDueDate.localeCompare(y.nextDueDate));
        const instances = Object.fromEntries(buildingChecks.map(check => [check.checkKey, check]));
        const components = currentBuilding?.model?.structure?.components ?? {};
        return Array.from(groupBy(visible, check => check.templateId).values()).map(checks => {
            const template = checks[0].template;
            const missing = template.componentSpecificType
                ? componentsMissingCheck(template.id, template.componentSpecificType, components, instances)
                : [];
            return { template, checks, missing };
        });
    })();

    // "Lägg till" in a group's "N {typ} saknar kontroll": one check per missing component, with
    // the template's defaults and the group's earliest next date, after a confirmation
    const [missingToAdd, setMissingToAdd] = useState<{ template: BuildingCheckTemplate; componentKeys: string[]; nextDueDate: string } | null>(null);
    const [addingMissing, setAddingMissing] = useState(false);
    const [addMissingError, setAddMissingError] = useState<string | null>(null);

    const handleAddMissing = async () => {
        if (!missingToAdd || !actualBuildingId) return;
        const { template, componentKeys, nextDueDate } = missingToAdd;
        setAddingMissing(true);
        setAddMissingError(null);
        try {
            await buildingChecksApi.createBuildingChecks(
                actualBuildingId,
                componentKeys.map(componentKey => ({ templateId: template.id, componentKey, startDate: nextDueDate, nextDueDate }))
            );
            setMissingToAdd(null);
            setAlert({ type: 'success', message: componentKeys.length === 1 ? 'Kontroll tillagd!' : `${componentKeys.length} kontroller tillagda!` });
            loadBuildingChecks(actualBuildingId);
        } catch (error) {
            console.error('Error adding missing checks:', error);
            setAddMissingError(error instanceof Error ? error.message : 'Kunde inte lägga till kontrollerna. Försök igen.');
        } finally {
            setAddingMissing(false);
        }
    };

    const toggleGroup = (templateId: string) => {
        setExpandedGroups(prev => {
            const next = new Set(prev);
            if (next.has(templateId)) next.delete(templateId); else next.add(templateId);
            return next;
        });
    };

    const renderGroup = ({ template, checks, missing }: typeof checkGroups[number]) => {
        // A template with a single check opens by itself
        const expanded = checks.length === 1 || expandedGroups.has(template.id);
        const first = checks[0];
        const dueStatus = getDueDateStatus(first.nextDueDate, template.advanceNotice);
        const typeName = template.componentSpecificType ? getTranslation(template.componentSpecificType).toLowerCase() : '';
        return (
            <React.Fragment key={template.id}>
                <tr className="check-group-row">
                    <td colSpan={5} className="check-cell-group">
                        <div className="check-group-header">
                            <button
                                type="button"
                                className="btn btn-link check-group-toggle"
                                onClick={() => toggleGroup(template.id)}
                                disabled={checks.length === 1}
                                aria-expanded={expanded}
                                aria-label={expanded ? `Dölj kontroller av ${template.name}` : `Visa kontroller av ${template.name}`}
                            >
                                {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                <strong>{template.name}</strong>
                                <span className="check-group-count">
                                    {checks.length} {checks.length === 1 ? 'kontroll' : 'kontroller'}
                                </span>
                            </button>
                            <span className="check-group-due">
                                Nästa {formatDate(first.nextDueDate)}{' '}
                                <span className={`check-due-text--${dueStatus}`}>({formatDaysUntilDue(first.nextDueDate)})</span>
                            </span>
                            {missing.length > 0 && (
                                <span className="check-group-missing">
                                    <AlertTriangle size={14} aria-hidden="true" />
                                    {missing.length} {typeName} saknar kontroll
                                    <Button
                                        size="sm"
                                        variant="outline-primary"
                                        className="ms-2"
                                        onClick={() => {
                                            setAddMissingError(null);
                                            // checks are sorted by due date: the first is the group's earliest
                                            setMissingToAdd({ template, componentKeys: missing, nextDueDate: first.nextDueDate });
                                        }}
                                    >
                                        <Plus size={14} className="me-1" />
                                        Lägg till
                                    </Button>
                                </span>
                            )}
                        </div>
                    </td>
                </tr>
                {expanded && checks.map(check => renderCheckRow(check, true))}
            </React.Fragment>
        );
    };

    if (!actualBuildingId && !loading) {
        return (
            <Container className="mt-4">
                <div className="text-center">
                    <Alert variant="danger">
                        <h4>Ingen byggnad vald</h4>
                        <p>Vänligen navigera till en byggnad för att visa aktiviteter.</p>
                    </Alert>
                </div>
            </Container>
        );
    }

    if (loading) {
        return (
            <Container className="mt-4">
                <div className="text-center">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Laddar...</span>
                    </div>
                    <p className="mt-2">Laddar aktiviteter...</p>
                </div>
            </Container>
        );
    }

    return (
        <>
            <div style={{ flexShrink: 0 }}>
                <BreadcrumbComponent
                    items={
                        actualBuildingId && currentBuilding
                            ? [
                                { text: currentBuilding.summary.name ?? 'Byggnad', link: `/building/${actualBuildingId}` },
                                { text: 'Kontroller' }
                            ]
                            : []
                    }
                />
            </div>
            <Container className="mt-4 building-checks-container" style={{ marginLeft: '20px', marginRight: '0', paddingLeft: '0', paddingRight: '0' }}>
                <div className="d-flex justify-content-between align-items-center mb-4 building-checks-header">
                <h1 className="page-title">Kontroller</h1>
                <div className="d-flex gap-2 align-items-center building-checks-actions">
                    <Form.Select
                        value={categoryFilter}
                        onChange={(e) => setCategoryFilter(e.target.value)}
                        className="w-auto"
                        style={{ minWidth: '160px' }}
                    >
                        <option value="">Alla kategorier</option>
                        {TEMPLATE_CATEGORIES.map(cat => (
                            <option key={cat.value} value={cat.value}>{cat.label}</option>
                        ))}
                    </Form.Select>
                    <Link
                        to="/settings/building-checks"
                        className="btn btn-outline-secondary"
                        style={{ paddingTop: '11.5px', paddingBottom: '11.5px' }}
                    >
                        <Settings size={16} className="me-1" />
                        Hantera mallar
                    </Link>
                    <Link
                        to={`/building/${buildingId}/checks/add`}
                        className="btn btn-primary"
                        title="Lägg till aktivitet"
                        style={{ backgroundColor: '#001D60', borderColor: '#001D60', paddingTop: '11.5px', paddingBottom: '11.5px' }}
                    >
                        <Plus size={16} className="me-1" />
                        Lägg till aktivitet
                    </Link>
                </div>
            </div>

            {alert && (
                <Alert variant={alert.type} dismissible onClose={() => setAlert(null)}>
                    {alert.message}
                </Alert>
            )}

            {buildingChecks.length === 0 ? (
                <div className="std-box dropshadow text-center py-5 px-3 building-checks-empty">
                    <Clock size={48} className="text-muted mb-3" />
                    <h5>Inga kontroller tillagda</h5>
                    <p className="text-muted">
                        Lägg till kontroller från kontrollmallarna för att börja följa upp dem.
                    </p>
                    <Link to={`/building/${buildingId}/checks/add`} className="btn btn-primary">
                        <Plus size={16} className="me-1" />
                        Lägg till första aktiviteten
                    </Link>
                </div>
            ) : (
                <div className="building-checks-table-card std-box dropshadow">
                        <Table responsive borderless className="building-checks-table">
                            <thead>
                                <tr>
                                    <th>Aktivitet</th>
                                    <th>Förfallodatum</th>
                                    <th>Senast genomförd</th>
                                    <th>Intervall</th>
                                    <th>Åtgärder</th>
                                </tr>
                            </thead>
                    <tbody>
                        {checkGroups.map(renderGroup)}
                    </tbody>
                        </Table>
                        {categoryFilter && buildingChecks.filter(check => {
                            return check.template?.category === categoryFilter;
                        }).length === 0 && (
                            <p className="text-muted text-center mt-3">Inga aktiviteter i denna kategori</p>
                        )}
                </div>
            )}


            {/* Completion Modal */}
            <Modal show={showCompletionModal} onHide={() => setShowCompletionModal(false)} size="lg" dialogClassName="building-checks-modal">
                <Modal.Header closeButton>
                    <Modal.Title>Markera aktivitet som klar</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {modalAlert && (
                        <Alert variant={modalAlert.type} className="mb-3">
                            {modalAlert.message}
                        </Alert>
                    )}
                    {checkToComplete && (
                        <>
                            {/* Recommendation to use mobile for component review */}
                            {checkToComplete.componentKey && (
                                <Alert variant="info" className="mb-3">
                                    <Alert.Heading as="h6" className="mb-1 d-flex align-items-center gap-2">
                                        <Smartphone size={18} />
                                        Använd mobilappen för fullständig granskning
                                    </Alert.Heading>
                                    <p className="mb-0 small">
                                        Skick och foton per komponent registreras i mobilappen. Här kan du snabbbekräfta kontrollen.
                                    </p>
                                </Alert>
                            )}

                            <CheckLocationSection buildingKey={actualBuildingId} check={checkToComplete} building={currentBuilding} />

                            <div className="mb-3">
                                <h6>Kontrollinformation</h6>
                                {checkToComplete.unitLabel && (
                                    <p className="mb-1"><strong>Lägenhet / lokal:</strong> {checkToComplete.unitLabel}</p>
                                )}
                                <p><strong>Namn:</strong> {checkName(checkToComplete, checkToComplete.template.name)}</p>
                                <p style={{ whiteSpace: 'pre-wrap' }}><strong>Beskrivning:</strong> {getEffectiveValues(checkToComplete, checkToComplete.template).description}</p>
                                <p><strong>Förfallodatum:</strong> {formatDate(checkToComplete.nextDueDate)}</p>
                            </div>

                            {/* Instructional Attachments (merged: template + building-specific) */}
                            {(() => {
                                const mergedAttachments = getEffectiveValues(checkToComplete, checkToComplete.template).attachments;
                                return mergedAttachments && Object.keys(mergedAttachments).length > 0 && (
                                    <div className="mb-3">
                                        <h6>Instruktionsbilagor</h6>
                                        <div className="d-flex flex-wrap gap-2">
                                            {Object.entries(mergedAttachments).map(([dbKey, attachment]) => (
                                                <Button
                                                    key={dbKey}
                                                    variant="outline-primary"
                                                    size="sm"
                                                    className="d-flex align-items-center"
                                                    onClick={() => {
                                                        const storage = getStorage();
                                                        const fileRef = storageRef(storage, attachment.storagePath);
                                                        getDownloadURL(fileRef).then((url) => {
                                                            window.open(url, '_blank');
                                                        }).catch((error) => {
                                                            console.error('Error getting download URL:', error);
                                                            setModalAlert({ type: 'danger', message: 'Fel vid hämtning av fil' });
                                                        });
                                                    }}
                                                >
                                                    <FileText size={14} className="me-1" />
                                                    {attachment.key}
                                                </Button>
                                            ))}
                                        </div>
                                        <small className="text-muted">Klicka på en bilaga för att öppna den i en ny flik</small>
                                    </div>
                                );
                            })()}

                            <Form.Group className="mb-3">
                                <Form.Label>När genomfördes aktiviteten? *</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={completionForm.completedAt}
                                    onChange={(e) => setCompletionForm(prev => ({ ...prev, completedAt: e.target.value }))}
                                    required
                                />
                                {completionForm.completedAt && checkToComplete && (
                                    <div className="mt-2">
                                        <small className="text-muted">
                                            Nästa förfallodatum beräknat på valt datum: {calculateNextDueDate(
                                                checkToComplete.template.nextDueDateCalculation,
                                                getEffectiveValues(checkToComplete, checkToComplete.template).interval,
                                                completionForm.completedAt,
                                                checkToComplete.nextDueDate
                                            )}
                                        </small>
                                    </div>
                                )}
                            </Form.Group>

                            {checkToComplete.template.takesMeasurement && (
                                <Form.Group className="mb-3">
                                    <Form.Label>Mätvärde ({checkToComplete.template.takesMeasurement.metric}) *</Form.Label>
                                    <Form.Control
                                        type="number"
                                        step="any"
                                        value={completionForm.measurementValue}
                                        onChange={(e) => setCompletionForm(prev => ({ ...prev, measurementValue: e.target.value }))}
                                        placeholder="Ange mätvärde"
                                        required
                                    />
                                    {checkToComplete.template.takesMeasurement.acceptableRange && (
                                        <Form.Text className="text-muted">
                                            Acceptabelt intervall: 
                                            {checkToComplete.template.takesMeasurement.acceptableRange.min !== undefined && 
                                             checkToComplete.template.takesMeasurement.acceptableRange.max !== undefined && (
                                                <span> {checkToComplete.template.takesMeasurement.acceptableRange.min} - {checkToComplete.template.takesMeasurement.acceptableRange.max}</span>
                                            )}
                                            {checkToComplete.template.takesMeasurement.acceptableRange.min !== undefined && 
                                             checkToComplete.template.takesMeasurement.acceptableRange.max === undefined && (
                                                <span> Min: {checkToComplete.template.takesMeasurement.acceptableRange.min}</span>
                                            )}
                                            {checkToComplete.template.takesMeasurement.acceptableRange.min === undefined && 
                                             checkToComplete.template.takesMeasurement.acceptableRange.max !== undefined && (
                                                <span> Max: {checkToComplete.template.takesMeasurement.acceptableRange.max}</span>
                                            )}
                                        </Form.Text>
                                    )}
                                </Form.Group>
                            )}

                            <Form.Group className="mb-3">
                                <Form.Label>Anteckningar</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={completionForm.notes}
                                    onChange={(e) => setCompletionForm(prev => ({ ...prev, notes: e.target.value }))}
                                    placeholder="Valfria anteckningar om genomförandet..."
                                />
                            </Form.Group>

                            {/* Photo Upload */}
                            <Form.Group className="mb-3">
                                <Form.Label>
                                    <Camera size={16} className="me-1" />
                                    Bilder (valfritt)
                                </Form.Label>
                                <div
                                    className={`completion-dropzone${isDragOver ? ' completion-dropzone--drag-over' : ''}${saving || isUploadingPhotos ? ' completion-dropzone--disabled' : ''}`}
                                    {...completionDragHandlers}
                                    onClick={() => !(saving || isUploadingPhotos) && completionFileInputRef.current?.click()}
                                >
                                    <input
                                        ref={completionFileInputRef}
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        onChange={handleCompletionFileSelect}
                                        style={{ display: 'none' }}
                                        disabled={saving || isUploadingPhotos}
                                    />
                                    <div className="completion-dropzone__text">
                                        {isDragOver ? (
                                            <span>Släpp bilder här</span>
                                        ) : (
                                            <span>
                                                Dra och släpp bilder här, eller klicka för att välja
                                                <br />
                                                <small>Max 10 bilder, 10 MB per bild</small>
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {/* Photo Previews */}
                                {completionPhotos.length > 0 && (
                                    <div className="completion-photos">
                                        {completionPhotos.map(photo => (
                                            <div key={photo.id} className="completion-photo">
                                                <img
                                                    src={photo.previewUrl}
                                                    alt="Preview"
                                                    className="completion-photo__image"
                                                />
                                                <button
                                                    type="button"
                                                    className="completion-photo__remove"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        removeCompletionPhoto(photo.id);
                                                    }}
                                                    disabled={saving || isUploadingPhotos}
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Form.Group>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowCompletionModal(false)} disabled={saving || isUploadingPhotos}>
                        Avbryt
                    </Button>
                    <Button
                        variant="success"
                        onClick={handleCompleteCheck}
                        disabled={!completionForm.completedAt || saving || isUploadingPhotos}
                    >
                        {saving || isUploadingPhotos ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                {uploadProgress
                                    ? `Laddar upp bild ${uploadProgress.current}/${uploadProgress.total}...`
                                    : 'Sparar...'
                                }
                            </>
                        ) : (
                            'Markera som klar'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Statistics Modal */}
            <Modal show={showStatsModal} onHide={() => setShowStatsModal(false)} size="xl" dialogClassName="building-checks-modal">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <BarChart3 size={20} className="me-2" />
                        Statistik och loggar - {checkForStats && checkName(checkForStats, checkForStats.template.name)}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {checkForStats && (
                        <>
                            {/* Where the check is: its component, or its place */}
                            <CheckLocationSection buildingKey={actualBuildingId} check={checkForStats} building={currentBuilding} />

                            {checkResults.length === 0 ? (
                                <div className="text-center py-4">
                                    <BarChart3 size={48} className="text-muted mb-3" />
                                    <h5>Inga resultat ännu</h5>
                                    <p className="text-muted">
                                        Denna aktivitet har inte genomförts ännu. Resultat och statistik kommer att visas här efter första genomförandet.
                                    </p>
                                </div>
                            ) : (
                                <>
                                    {/* Chart for measurements */}
                                    {checkForStats.template.takesMeasurement && checkResults.some(r => r.measurement) && (
                                        <div className="mb-4">
                                            <h6>Mätvärden över tid</h6>
                                            <div style={{ height: '300px' }}>
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <BarChart
                                                        data={checkResults
                                                            .filter(r => r.measurement)
                                                            .map(r => ({
                                                                date: new Date(r.completedAt).toLocaleDateString('sv-SE'),
                                                                value: r.measurement?.value || 0,
                                                                withinRange: r.measurement?.withinRange || false
                                                            }))
                                                            .reverse() // Show oldest first
                                                        }
                                                        margin={isSmallScreen
                                                            ? { top: 20, right: 8, left: 0, bottom: 5 }
                                                            : { top: 20, right: 30, left: 20, bottom: 5 }}
                                                    >
                                                        <CartesianGrid strokeDasharray="3 3" />
                                                        <XAxis
                                                            dataKey="date"
                                                            angle={-45}
                                                            textAnchor="end"
                                                            height={80}
                                                            tick={isSmallScreen ? { fontSize: 12 } : true}
                                                        />
                                                        <YAxis
                                                            label={{ value: checkForStats.template.takesMeasurement?.metric || '', angle: -90, position: 'insideLeft' }}
                                                            tick={isSmallScreen ? { fontSize: 12 } : true}
                                                            width={isSmallScreen ? 56 : 60}
                                                        />
                                                        <Tooltip 
                                                            formatter={(value: any, name: string) => [
                                                                `${value} ${checkForStats.template.takesMeasurement?.metric || ''}`,
                                                                'Värde'
                                                            ]}
                                                            labelFormatter={(label) => `Datum: ${label}`}
                                                        />
                                                        <Bar 
                                                            dataKey="value" 
                                                            fill="#8884d8"
                                                            name="Mätvärde"
                                                        />
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            </div>
                                        </div>
                                    )}

                                    {/* Results table */}
                                    <div>
                                        <h6>Alla genomföranden ({checkResults.length})</h6>
                                        <Table responsive striped size="sm" className="check-results-table">
                                            <thead>
                                                <tr>
                                                    <th>Datum</th>
                                                    <th>Genomförd av</th>
                                                    {checkForStats.template.takesMeasurement && <th>Mätvärde</th>}
                                                    <th>Anteckningar</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {checkResults.map((result, index) => (
                                                    <tr key={index}>
                                                        <td className="result-cell-date">{formatDate(result.completedAt)}</td>
                                                        <td className="result-cell-by">
                                                            <div>
                                                                <span className="check-cell-label">Genomförd av </span>
                                                                {userNames[result.completedBy] || 'Okänd användare'}
                                                            </div>
                                                            <small className="text-muted">
                                                                {new Date(result.submittedAt).toLocaleDateString('sv-SE')} {new Date(result.submittedAt).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' })}
                                                            </small>
                                                        </td>
                                                        {checkForStats.template.takesMeasurement && (
                                                            <td className="result-cell-value">
                                                                {result.measurement ? (
                                                                    <div>
                                                                        <span className={result.measurement.withinRange ? 'text-success' : 'text-danger'}>
                                                                            {result.measurement.value} {result.measurement.unit}
                                                                        </span>
                                                                        {!result.measurement.withinRange && (
                                                                            <Badge bg="danger" className="ms-1 small">Utanför gränsvärde</Badge>
                                                                        )}
                                                                    </div>
                                                                ) : (
                                                                    <span className="text-muted">-</span>
                                                                )}
                                                            </td>
                                                        )}
                                                        <td className={`result-cell-notes ${result.summaryComment ? '' : 'result-cell-empty'}`}>
                                                            {result.summaryComment ? (
                                                                <small>{result.summaryComment}</small>
                                                            ) : (
                                                                <span className="text-muted">-</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowStatsModal(false)}>
                        Stäng
                    </Button>
                </Modal.Footer>
            </Modal>

            <ConfirmationModal
                show={!!missingToAdd}
                title="Lägg till kontroller"
                message={missingToAdd
                    ? `Lägg till ${missingToAdd.componentKeys.length} ${missingToAdd.componentKeys.length === 1 ? 'kontroll' : 'kontroller'} av ${missingToAdd.template.name}, nästa datum ${missingToAdd.nextDueDate}.`
                    : ''}
                confirmText="Lägg till"
                variant="primary"
                onConfirm={handleAddMissing}
                onCancel={() => { if (!addingMissing) setMissingToAdd(null); }}
                loading={addingMissing}
                error={addMissingError}
            />

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} dialogClassName="building-checks-modal">
                <Modal.Header closeButton>
                    <Modal.Title>Bekräfta radering</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {checkToDelete && (
                        <>
                            <p>Är du säker på att du vill radera aktiviteten <strong>"{checkName(checkToDelete, checkToDelete.template.name)}"</strong>?</p>
                            <Alert variant="info" className="mt-3">
                                <strong>Obs:</strong> Eventuell historik kommer att finnas kvar även efter radering.
                            </Alert>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Avbryt
                    </Button>
                    <Button
                        variant="danger"
                        onClick={handleDeleteCheck}
                        disabled={saving}
                    >
                        {saving ? 'Raderar...' : 'Radera'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Snooze Modal */}
            <Modal show={showSnoozeModal} onHide={() => setShowSnoozeModal(false)} dialogClassName="building-checks-modal">
                <Modal.Header closeButton>
                    <Modal.Title>Flytta förfallodatum</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {checkToSnooze && (
                        <>
                            <p>Flytta förfallodatum för <strong>"{checkName(checkToSnooze, checkToSnooze.template.name)}"</strong></p>
                            <p className="text-muted small">
                                Nuvarande förfallodatum: {formatDate(checkToSnooze.nextDueDate)}
                            </p>
                            <Form.Group className="mb-3">
                                <Form.Label>Nytt förfallodatum</Form.Label>
                                <Form.Control
                                    type="date"
                                    value={snoozeDate}
                                    onChange={(e) => setSnoozeDate(e.target.value)}
                                    min={(() => {
                                        const tomorrow = new Date();
                                        tomorrow.setDate(tomorrow.getDate() + 1);
                                        return formatDateOnlyForStorage(tomorrow);
                                    })()}
                                />
                                <Form.Text className="text-muted">
                                    Datum måste vara efter nuvarande förfallodatum.
                                </Form.Text>
                            </Form.Group>
                            <Alert variant="info" className="mb-0">
                                <strong>Hur påverkas framtida datum?</strong>
                                {checkToSnooze.template.nextDueDateCalculation === 'scheduled' ? (
                                    <p className="mb-0 mt-1">
                                        Denna aktivitet är schemalagd. Flytten påverkar alla framtida förfallodatum –
                                        nästa förekomst beräknas från det nya datumet.
                                    </p>
                                ) : (
                                    <p className="mb-0 mt-1">
                                        Denna aktivitet beräknas utifrån utförande. Flytten påverkar bara detta
                                        förfallodatum – nästa beräknas när aktiviteten markeras som klar.
                                    </p>
                                )}
                            </Alert>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowSnoozeModal(false)}>
                        Avbryt
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleSnoozeCheck}
                        disabled={!snoozeDate || saving}
                    >
                        {saving ? 'Sparar...' : 'Flytta datum'}
                    </Button>
                </Modal.Footer>
            </Modal>
            </Container>
        </>
    );
};

export default BuildingChecksPage;
